import React from 'react'
import { cn } from '@/lib/utils'
import '../sections/control-targets3.css'

/**
 * GuardrailBand (W6, Control) — a horizontal rate band between a floor and a ceiling,
 * with a proposed-rate dot that travels once to the ceiling and is visibly CLAMPED
 * there (it can't cross the guardrail, whatever the demand signal says).
 *
 * Copy is quoted verbatim from docs/marketing/2026-07-05-landing3-copy.md §W6.
 * £80 / £160 are illustrative bounds (per-property configurable — no defaults implied);
 * every money figure in this widget is illustrative fiction for the demo vignette.
 * Animates ONCE on reveal via an IntersectionObserver `is-live` gate; the designed
 * still (dot already held at the ceiling) is the first-paint default and the
 * prefers-reduced-motion fallback (both handled in control-targets3.css).
 */

// Value → position along the visible track. Floor sits at 8%, ceiling at 92%, leaving
// room for the end markers; the dot never renders outside [floor, ceiling].
const FLOOR = 92 // ceiling position (%), i.e. where the clamp holds the dot
const START = '46%' // the dot's opening position (an in-bounds mid-band rate)

export default function GuardrailBand({ className }: { className?: string }) {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const [live, setLive] = React.useState(false)

  React.useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setLive(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setLive(true)
          io.disconnect()
        }
      },
      { threshold: 0.4 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      role="img"
      aria-label="A price band between a floor of £80 and a ceiling of £160, with a proposed rate held at the ceiling."
      className={cn('mkt3-panel p-5 sm:p-6', className)}
      style={
        {
          '--mkt3-ctm-clamp': `${FLOOR}%`,
          '--mkt3-ctm-start': START,
        } as React.CSSProperties
      }
    >
      <div className="mb-6 flex items-center justify-between">
        <span className="mkt3-label">Price Guardrails</span>
      </div>

      {/* The band. Floor and ceiling are the hard walls; the fill shows the allowed range. */}
      <div className="relative pt-9 pb-2" aria-hidden="true">
        {/* proposed-rate dot with a value flag above it */}
        <div className={cn('mkt3-ctm-dot absolute top-0 -translate-x-1/2', live && 'is-live')}>
          <span className="mb-1.5 block whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-center font-mono text-[11px] font-semibold tabular-nums text-white">
            £160
          </span>
          <span className="mx-auto block h-3 w-3 rounded-full border-2 border-white bg-teal-600 shadow-[0_2px_8px_-1px_rgba(13,148,136,0.6)]" />
          <span className="absolute left-1/2 top-3 h-3 w-px -translate-x-1/2 bg-slate-300" />
        </div>

        {/* the track */}
        <div className="relative h-2.5 rounded-full bg-stone-100">
          <span
            className="absolute inset-y-0 left-[8%] right-[8%] rounded-full bg-gradient-to-r from-teal-100 via-teal-200/70 to-teal-300/60"
            aria-hidden="true"
          />
          {/* floor wall */}
          <span className="absolute left-[8%] top-1/2 h-5 w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-slate-400" />
          {/* ceiling wall — flashes once when the dot lands */}
          <span
            className={cn(
              'mkt3-ctm-wall absolute left-[92%] top-1/2 h-5 w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-rose-500',
              live && 'is-live'
            )}
          />
        </div>

        {/* end markers */}
        <div className="mt-3 flex items-center justify-between font-mono text-[11px] tabular-nums">
          <span className="text-slate-500">
            Floor <span className="font-semibold text-slate-700">£80</span>
          </span>
          <span className="text-rose-600">
            Ceiling <span className="font-semibold">£160</span>
          </span>
        </div>
      </div>

      <p
        className={cn(
          'mkt3-ctm-held mt-4 inline-flex items-center gap-2 text-sm font-semibold text-slate-900',
          live && 'is-live'
        )}
      >
        <span aria-hidden="true" className="mkt3-live-dot h-1.5 w-1.5 rounded-full bg-rose-500" />
        Held at your ceiling — £160
      </p>
    </div>
  )
}
